import { useEffect, useRef } from "react";
import flatpickr from "flatpickr";
import "flatpickr/dist/flatpickr.min.css";
import "./datepicker.css";

const INPUT_CLASS =
  "w-full rounded-[18px] border border-[#dce1ef] bg-[#fbfbfe] px-4 py-3 pr-12 text-sm " +
  "text-[#111425] shadow-[0_14px_30px_-24px_rgba(23,30,67,0.42)] transition placeholder:text-[#7c839f] " +
  "focus:border-[#282E6A] focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#282E6A]/15";

function DateTimePicker({ value, onChange, placeholder }) {
  const inputRef = useRef(null);
  const fpRef = useRef(null);
  const prevValueRef = useRef(null);
  // Ref so flatpickr's stable callback always calls the latest handler
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // Create the flatpickr instance exactly once
  useEffect(() => {
    const fp = flatpickr(inputRef.current, {
      enableTime: true,
      dateFormat: "M j, Y h:i K",
      minuteIncrement: 15,
      disableMobile: true,
      closeOnSelect: false,
      defaultDate: value || undefined,
      onOpen() {
        // Snapshot value so Cancel can restore it
        prevValueRef.current = fp.selectedDates[0] ?? null;
      },
      onChange(dates) {
        onChangeRef.current(dates[0] ?? null);
      },
    });
    fpRef.current = fp;

    // Inject Cancel + OK row at the bottom of the calendar
    const row = document.createElement("div");
    row.className = "flatpickr-button-row";

    const cancelBtn = document.createElement("span");
    cancelBtn.className = "flatpickr-cancel";
    cancelBtn.textContent = "Cancel";
    cancelBtn.addEventListener("click", () => {
      const prev = prevValueRef.current;
      if (prev) {
        fp.setDate(prev, false);
        onChangeRef.current(prev);
      } else {
        fp.clear(false);
        onChangeRef.current(null);
      }
      fp.close();
    });

    const okBtn = document.createElement("span");
    okBtn.className = "flatpickr-confirm";
    okBtn.textContent = "OK";
    okBtn.addEventListener("click", () => fp.close());

    row.appendChild(cancelBtn);
    row.appendChild(okBtn);
    fp.calendarContainer.appendChild(row);

    return () => fp.destroy();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync external value changes (e.g. parent resets or pre-fills)
  useEffect(() => {
    const fp = fpRef.current;
    if (!fp) return;
    if (value) {
      fp.setDate(value, false);
    } else {
      fp.clear(false);
    }
  }, [value]);

  return (
    <div className="relative">
      <input
        ref={inputRef}
        type="text"
        placeholder={placeholder ?? "Select date & time..."}
        className={INPUT_CLASS}
        readOnly
        inputMode="none"
        aria-haspopup="dialog"
      />
      <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
        <span className="flex h-8 w-8 items-center justify-center rounded-full border border-[#e4e8f4] bg-white text-[#282E6A] shadow-[0_10px_18px_-16px_rgba(23,30,67,0.8)]">
          <svg
            aria-hidden="true"
            viewBox="0 0 24 24"
            className="h-4 w-4"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M7 3v3M17 3v3M4 9h16M6 5h12a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2Z" />
          </svg>
        </span>
      </div>
    </div>
  );
}

export default DateTimePicker;
